import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Icon from '../../../components/AppIcon';
import SplitPanelContent from '../../../components/SplitPanelContent';

const DEFAULT_CONFIG = {
  panelSide: 'left',
  widthRatio: 40,
  contentType: 'image',
  imageUrl: '',
  imageObjectFit: 'cover',
  overlayCaption: '',
  videoUrl: '',
  richText: '',
};

const WIDTH_PRESETS = [[30, 70], [40, 60], [50, 50], [60, 40]];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'color', 'list', 'bullet'];

const SplitPanelSettings = ({ config, onUpdate }) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };

  const update = (patch) => onUpdate({ ...cfg, ...patch });

  return (
    <div className="space-y-4 pt-3 border-t border-border">
      <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
        <Icon name="PanelLeft" size={13} />
        Split Panel Configuration
      </p>

      {/* ── Content Type ── */}
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Content Type</p>
        <div className="flex gap-1">
          {[
            { value: 'image', label: 'Image', icon: 'Image' },
            { value: 'video', label: 'Video', icon: 'Play' },
            { value: 'text', label: 'Text', icon: 'AlignLeft' },
          ].map(({ value, label, icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => update({ contentType: value })}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium rounded-md border-2 transition-colors ${
                cfg.contentType === value
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/40 text-foreground'
              }`}
            >
              <Icon name={icon} size={11} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Panel Position ── */}
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Panel Position</p>
        <div className="flex gap-1">
          {[
            { value: 'left', label: 'Left', icon: 'PanelLeft' },
            { value: 'right', label: 'Right', icon: 'PanelRight' },
          ].map(({ value, label, icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => update({ panelSide: value })}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md border-2 transition-colors ${
                cfg.panelSide === value
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/40 text-foreground'
              }`}
            >
              <Icon name={icon} size={11} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Width Ratio ── */}
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Width Ratio <span className="opacity-60">(panel / form)</span></p>
        <div className="flex gap-1 flex-wrap mb-2">
          {WIDTH_PRESETS.map(([panel, form]) => (
            <button
              key={panel}
              type="button"
              onClick={() => update({ widthRatio: panel })}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border-2 transition-colors ${
                cfg.widthRatio === panel
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/40 text-foreground'
              }`}
            >
              {panel}/{form}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={20}
            max={80}
            value={cfg.widthRatio}
            onChange={(e) => {
              const v = Math.min(80, Math.max(20, parseInt(e.target.value, 10) || 40));
              update({ widthRatio: v });
            }}
            className="w-20 px-2 py-1 text-xs border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="text-[10px] text-muted-foreground">% panel (20–80)</span>
        </div>
      </div>

      {/* ── Image Controls ── */}
      {cfg.contentType === 'image' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-foreground block mb-1">Image URL</label>
            <input
              type="text"
              value={cfg.imageUrl}
              onChange={(e) => update({ imageUrl: e.target.value })}
              placeholder="https://example.com/image.jpg"
              className="w-full px-2.5 py-1.5 text-xs border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          {cfg.imageUrl && (
            <div className="rounded-md overflow-hidden border border-border bg-muted/30" style={{ height: 100 }}>
              <img
                src={cfg.imageUrl}
                alt="Preview"
                className="w-full h-full"
                style={{ objectFit: cfg.imageObjectFit || 'cover' }}
                onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
              />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-foreground block mb-1">Object Fit</label>
            <div className="flex gap-1">
              {['cover', 'contain'].map((fit) => (
                <button
                  key={fit}
                  type="button"
                  onClick={() => update({ imageObjectFit: fit })}
                  className={`flex-1 py-1 text-xs font-medium rounded-md border-2 transition-colors ${
                    cfg.imageObjectFit === fit
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/40 text-foreground'
                  }`}
                >
                  {fit.charAt(0).toUpperCase() + fit.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground block mb-1">
              Overlay Caption <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <input
              type="text"
              value={cfg.overlayCaption}
              onChange={(e) => update({ overlayCaption: e.target.value })}
              placeholder="Caption shown over image..."
              className="w-full px-2.5 py-1.5 text-xs border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      )}

      {/* ── Video Controls ── */}
      {cfg.contentType === 'video' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-foreground block mb-1">Video URL</label>
            <input
              type="text"
              value={cfg.videoUrl}
              onChange={(e) => update({ videoUrl: e.target.value })}
              placeholder="YouTube, Vimeo, or .mp4/.webm URL..."
              className="w-full px-2.5 py-1.5 text-xs border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Supports YouTube, Vimeo, and direct video file URLs
            </p>
          </div>
          {cfg.videoUrl && (
            <div className="rounded-md overflow-hidden border border-border bg-muted/30" style={{ height: 100 }}>
              <SplitPanelContent config={{ contentType: 'video', videoUrl: cfg.videoUrl }} preview />
            </div>
          )}
        </div>
      )}

      {/* ── Rich Text Controls ── */}
      {cfg.contentType === 'text' && (
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Text Content</label>
          <div
            className="border border-border rounded-md overflow-hidden"
            style={{ fontSize: 12 }}
          >
            <ReactQuill
              theme="snow"
              value={cfg.richText || ''}
              onChange={(val) => update({ richText: val })}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SplitPanelSettings;
